import { Typography } from '@braskai/ui-kit';

import { Box, Stack } from '@mui/material';

import { CUSTOM_PLAN_DATA } from '@/widgets/pricing/pricingCards/lib/constants';
import { getFormattedPlanPrice } from '@/widgets/pricing/pricingCards/lib/utils/getFormattedPlanPrice';

import { getDiscountedYearlyUnitAmount } from '@/shared/constants/pricingDiscount';
import { formatCentsToDollars } from '@/shared/lib/format';
import { PaymentInterval, Plan, PlanCategory, Price as PriceType } from '@/shared/types/Billing';

import { MinutesText } from '../MinutesText';

type PriceBlockProps = {
  minutesValue: number | string;
  plan: Plan;
  selectedPaymentPeriod: PaymentInterval;
};

export const PriceBlock = ({ minutesValue, plan, selectedPaymentPeriod }: PriceBlockProps) => {
  const { category } = plan;
  const isEnterprise = category === PlanCategory.Enterprise;
  const isCustomPlan = plan.id === CUSTOM_PLAN_DATA.id || isEnterprise;

  const priceColor = isEnterprise ? 'common.white' : 'grey.900';
  const mutedColor = isEnterprise ? 'grey.300' : 'grey.500';

  const activePrices = plan.prices.filter((price) => price.status === 'active');
  const findPrice = (interval: PaymentInterval): PriceType | undefined =>
    activePrices.find((price) => price.recurring?.interval === interval);

  const monthlyPrice = findPrice(PaymentInterval.Month);
  const yearPrice = findPrice(PaymentInterval.Year);
  const isYearlySelected = selectedPaymentPeriod === PaymentInterval.Year;

  const displayPrice = (isYearlySelected ? yearPrice : monthlyPrice) ?? yearPrice ?? monthlyPrice;
  const isDisplayPriceYearly = displayPrice?.recurring?.interval === PaymentInterval.Year;
  const getEffectiveUnitAmount = (price: PriceType) =>
    price.recurring?.interval === PaymentInterval.Year
      ? getDiscountedYearlyUnitAmount(category, price.unit_amount)
      : price.unit_amount;
  const perMonth = displayPrice
    ? getFormattedPlanPrice({
        paymentInterval: displayPrice.recurring?.interval,
        price: getEffectiveUnitAmount(displayPrice),
      })
    : 0;

  const monthlyPerMonth = monthlyPrice
    ? getFormattedPlanPrice({ paymentInterval: PaymentInterval.Month, price: monthlyPrice.unit_amount })
    : undefined;
  const strikePerMonth = monthlyPerMonth !== undefined && monthlyPerMonth > perMonth ? monthlyPerMonth : undefined;

  const annualTotalCents = yearPrice ? getEffectiveUnitAmount(yearPrice) : undefined;

  // Figma: annual shows the yearly total, monthly shows a plain "billed monthly" line.
  const getBilledLabel = () => {
    if (!displayPrice) return undefined;
    if (!isDisplayPriceYearly) return 'billed monthly';
    if (annualTotalCents !== undefined) return `billed $${formatCentsToDollars(annualTotalCents)} yearly`;

    return undefined;
  };
  const billedLabel = getBilledLabel();

  return (
    <Stack gap={12}>
      <Stack gap={4}>
        <Stack alignItems="flex-end" direction="row" gap={4}>
          {isCustomPlan ? (
            <Typography color={priceColor} variant="title-2-sb">
              Custom
            </Typography>
          ) : (
            <>
              {strikePerMonth !== undefined && (
                <Typography color="grey.300" sx={{ textDecoration: 'line-through' }} variant="title-2-md">
                  {`$${strikePerMonth}`}
                </Typography>
              )}
              <Typography color={priceColor} variant="title-2-sb">
                {`$${perMonth}`}
              </Typography>
              <Typography color={mutedColor} sx={{ paddingBottom: 2 }} variant="body-2-md">
                /mo
              </Typography>
            </>
          )}
        </Stack>
        {!isCustomPlan && billedLabel ? (
          <Typography color={mutedColor} variant="caption-1-md">
            {billedLabel}
          </Typography>
        ) : (
          <Box sx={{ height: 16 }} />
        )}
      </Stack>
      <MinutesText category={category} value={minutesValue} />
    </Stack>
  );
};
